/**
 * API client service for Test Case Management Tool
 * Handles all HTTP requests to the backend API with authentication headers
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

// Types
export interface Folder {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  link_token: string;
  parent_id?: string;
  level: number;
  folder_type: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  shareable_link?: string;
  test_case_count: number;
  test_scenario_count: number;
  review_status?: string;
  reviewer_email?: string;
  submitted_by?: string;
  submitted_at?: string;
  reviewed_at?: string;
  epic_id?: string;
}

export interface FolderCreate {
  name: string;
  description?: string;
  icon?: string;
  parent_id?: string;
  folder_type?: string;
  epic_id?: string;
}

export interface FolderUpdate {
  name: string;
  description?: string;
  icon?: string;
  folder_type?: string;
  epic_id?: string;
}

export interface FolderTreeNode extends Folder {
  children: FolderTreeNode[];
}

export interface FolderBreadcrumb {
  id: string;
  name: string;
  level: number;
}

export interface FolderLink {
  folder_id: string;
  link_token: string;
  shareable_link: string;
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

export interface TestScenario {
  id: string;
  scenario_id: string;  // TS-001, TS-002, etc.
  folder_id: string;
  scenario_description: string;
  preconditions?: string;
  expected_result?: string;  // Made optional
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  module_name?: string;
  environment?: 'Stage' | 'Preprod' | 'Prod';
  tags: string[];
  version: number;
  is_latest: boolean;
  parent_scenario_id?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface TestScenarioWithEvidence extends TestScenario {
  expected_evidence: string[];
}

export interface TestScenarioCreate {
  folder_id: string;
  scenario_description: string;
  preconditions?: string;
  expected_result?: string;  // Made optional
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  module_name?: string;
  environment?: 'Stage' | 'Preprod' | 'Prod';
  tags: string[];
}

export interface TestScenarioUpdate {
  scenario_description: string;
  preconditions?: string;
  expected_result?: string;  // Made optional
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  module_name?: string;
  environment?: 'Stage' | 'Preprod' | 'Prod';
  tags: string[];
}

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

export interface TestCase {
  id: string;
  test_case_id: string;  // TC-001, TC-002, etc.
  folder_id: string;
  scenario_id?: string;
  scenario_version?: number;
  test_case_description: string;  // Case
  preconditions?: string;  // Precondition
  priority: 'Critical' | 'High' | 'Medium' | 'Low';  // Priority
  steps?: string;  // Steps (multi-line)
  expected_result: string;  // Expected
  actual_result?: string;  // Actual (filled during execution)
  screenshot_path?: string;  // Screenshot attachment
  tags: string[];  // Tags (e.g., Regression, Smoke)
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface TestCaseExportRequest {
  scenario_ids?: string[];
  folder_id?: string;
  tags?: string[];
}

// ==================== Test Run & Execution Types (Enterprise) ====================

export interface TestRun {
  id: string;
  folder_id: string;
  name: string;
  description?: string;
  run_type?: 'SCENARIO' | 'TEST_CASE';
  build_version?: string;
  environment?: string;
  cycle_label?: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'ABORTED';
  created_at: string;
  created_by: string;
  started_at?: string;
  started_by?: string;
  completed_at?: string;
  completed_by?: string;
}

export interface TestRunWithProgress extends TestRun {
  total_cases: number;
  not_run_count: number;
  pass_count: number;
  fail_count: number;
  blocked_count: number;
  executed_cases: number;
  progress_percentage: number;
  pass_percentage: number;
}

export interface TestRunCreate {
  name: string;
  description?: string;
  folder_id: string;
  run_type?: 'SCENARIO' | 'TEST_CASE';
  build_version?: string;
  environment?: string;
}

export interface TestExecution {
  id: string;
  test_run_id: string;
  test_case_id: string;
  execution_status: 'NOT_RUN' | 'PASS' | 'FAIL' | 'BLOCKED';
  comment?: string;
  defect_reference?: string;
  executed_at?: string;
  executed_by?: string;
}

export interface TestExecutionUpdate {
  execution_status: 'PASS' | 'FAIL' | 'BLOCKED';
  comment?: string;
  defect_reference?: string;
}

export interface StageEvidence {
  id: string;
  execution_id: string;
  file_path: string;
  file_name: string;
  display_order: number;
  uploaded_at: string;
  uploaded_by: string;
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * API Client class
 * Manages HTTP requests with JWT authentication and error handling
 */
class ApiClient {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string | null> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add JWT authentication
    this.client.interceptors.request.use(
      (config) => {
        // Get JWT token from localStorage
        const authTokens = localStorage.getItem('auth_tokens');
        if (authTokens) {
          try {
            const tokens = JSON.parse(authTokens);
            config.headers['Authorization'] = `Bearer ${tokens.access_token}`;
          } catch (error) {
            console.error('Error parsing auth tokens:', error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as any;

        // If 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const newAccessToken = await this.refreshToken();
            if (newAccessToken) {
              // Retry the original request with new token
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Refresh the access token using the refresh token
   */
  private async refreshToken(): Promise<string | null> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      try {
        const authTokens = localStorage.getItem('auth_tokens');
        if (!authTokens) {
          return null;
        }

        const tokens = JSON.parse(authTokens);
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
          refresh_token: tokens.refresh_token,
        });

        const newTokens = {
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          token_type: response.data.token_type,
        };

        localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
        return newTokens.access_token;
      } catch (error) {
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('auth_user');
        return null;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  /**
   * Handle API errors and format them consistently
   */
  private handleError(error: AxiosError<any>): Error {
    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data;
      
      // Check for FastAPI error format (detail field)
      if (responseData?.detail) {
        return new Error(responseData.detail);
      }
      
      // Check for custom error format
      const apiError = responseData?.error;
      if (apiError) {
        return new Error(apiError.message || 'An error occurred');
      }
      
      return new Error(`Server error: ${error.response.status}`);
    } else if (error.request) {
      // Request made but no response received
      return new Error('No response from server. Please check your connection.');
    } else {
      // Error in request setup
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // Generic HTTP Methods
  
  /**
   * Generic GET request
   */
  async get<T = any>(url: string, config?: any): Promise<{ data: T }> {
    return this.client.get<T>(url, config);
  }

  /**
   * Generic POST request
   */
  async post<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    return this.client.post<T>(url, data, config);
  }

  /**
   * Generic PUT request
   */
  async put<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    return this.client.put<T>(url, data, config);
  }

  /**
   * Generic DELETE request
   */
  async delete<T = any>(url: string, config?: any): Promise<{ data: T }> {
    return this.client.delete<T>(url, config);
  }

  // Folder API Methods

  /**
   * Create a new folder
   */
  async createFolder(data: FolderCreate): Promise<Folder> {
    const response = await this.client.post<Folder>('/api/v1/folders', data);
    return response.data;
  }

  /**
   * Get all folders, optionally filtered by type
   */
  async getFolders(folderType?: 'test_case' | 'test_scenario', rootOnly: boolean = false): Promise<Folder[]> {
    const params = new URLSearchParams();
    if (rootOnly) params.append('root_only', 'true');
    if (folderType) params.append('folder_type', folderType);
    const queryString = params.toString();
    const response = await this.client.get<Folder[]>(`/api/v1/folders${queryString ? '?' + queryString : ''}`);
    return response.data;
  }

  /**
   * Get folder tree structure
   */
  async getFolderTree(rootId?: string, folderType?: 'test_case' | 'test_scenario'): Promise<FolderTreeNode[]> {
    const params = new URLSearchParams();
    if (rootId) params.append('root_id', rootId);
    if (folderType) params.append('folder_type', folderType);
    const queryString = params.toString();
    const response = await this.client.get<FolderTreeNode[]>(`/api/v1/folders/tree${queryString ? '?' + queryString : ''}`);
    return response.data;
  }

  /**
   * Get subfolders of a folder
   */
  async getSubfolders(folderId: string): Promise<Folder[]> {
    const response = await this.client.get<Folder[]>(`/api/v1/folders/${folderId}/subfolders`);
    return response.data;
  }

  /**
   * Get breadcrumb trail for a folder
   */
  async getBreadcrumb(folderId: string): Promise<FolderBreadcrumb[]> {
    const response = await this.client.get<FolderBreadcrumb[]>(`/api/v1/folders/${folderId}/breadcrumb`);
    return response.data;
  }

  /**
   * Get a folder by ID
   */
  async getFolder(folderId: string): Promise<Folder> {
    const response = await this.client.get<Folder>(`/api/v1/folders/${folderId}`);
    return response.data;
  }

  /**
   * Update a folder
   */
  async updateFolder(folderId: string, data: FolderUpdate): Promise<Folder> {
    const response = await this.client.put<Folder>(`/api/v1/folders/${folderId}`, data);
    return response.data;
  }

  async moveFolder(folderId: string, newParentId: string | null): Promise<Folder> {
    const params = new URLSearchParams();
    if (newParentId) params.append('new_parent_id', newParentId);
    const response = await this.client.patch<Folder>(`/api/v1/folders/${folderId}/move?${params.toString()}`);
    return response.data;
  }

  async submitForReview(folderId: string, reviewerEmail: string, comment?: string): Promise<Folder> {
    const response = await this.client.post<Folder>(`/api/v1/folders/${folderId}/submit-review`, { reviewer_email: reviewerEmail, comment });
    return response.data;
  }

  async reviewAction(folderId: string, action: 'approve' | 'request_changes', comment?: string): Promise<Folder> {
    const response = await this.client.post<Folder>(`/api/v1/folders/${folderId}/review-action`, { action, comment });
    return response.data;
  }

  async getFolderComments(folderId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/api/v1/folders/${folderId}/comments`);
    return response.data;
  }

  async addFolderComment(folderId: string, comment: string): Promise<any> {
    const response = await this.client.post<any>(`/api/v1/folders/${folderId}/comments`, { comment });
    return response.data;
  }

  async getMyReviews(): Promise<{ in_review: any[]; completed: any[] }> {
    const response = await this.client.get('/api/v1/reviews/my-reviews');
    return response.data;
  }

  async getUsersForReview(): Promise<{ email: string; name: string; role: string }[]> {
    const response = await this.client.get('/api/v1/admin/users/for-review');
    return response.data;
  }

  /**
   * Delete a folder
   */
  async deleteFolder(folderId: string): Promise<void> {
    await this.client.delete(`/api/v1/folders/${folderId}`);
  }

  /**
   * Get shareable link for a folder
   */
  async getFolderLink(folderId: string): Promise<FolderLink> {
    const response = await this.client.get<FolderLink>(`/api/v1/folders/${folderId}/link`);
    return response.data;
  }

  // Scenario API Methods

  /**
   * Create a new test scenario
   */
  async createScenario(data: TestScenarioCreate): Promise<TestScenario> {
    const response = await this.client.post<TestScenario>('/api/v1/scenarios', data);
    return response.data;
  }

  /**
   * Get a scenario by ID
   */
  async getScenario(scenarioId: string): Promise<TestScenarioWithEvidence> {
    const response = await this.client.get<TestScenarioWithEvidence>(`/api/v1/scenarios/${scenarioId}`);
    return response.data;
  }

  /**
   * Get scenarios by folder ID
   */
  async getScenariosByFolder(folderId: string): Promise<TestScenarioWithEvidence[]> {
    const response = await this.client.get<TestScenarioWithEvidence[]>(`/api/v1/scenarios?folder_id=${folderId}`);
    return response.data;
  }

  /**
   * Search all folders by Epic ID
   */
  async searchFoldersByEpic(epicId: string): Promise<{
    epic_id: string;
    total: number;
    folders: {
      folder_id: string;
      folder_name: string;
      folder_path: string;
      project_name: string;
      folder_type: string;
      review_status: string;
      epic_id: string;
    }[];
  }> {
    const response = await this.client.get(`/api/v1/folders/epic-search?epic_id=${encodeURIComponent(epicId)}`);
    return response.data;
  }

  /**
   * Set or clear the Epic ID on a folder
   */
  async setFolderEpicId(folderId: string, epicId: string | null): Promise<Folder> {
    const params = epicId ? `?epic_id=${encodeURIComponent(epicId)}` : '';
    const response = await this.client.patch(`/api/v1/folders/${folderId}/epic-id${params}`);
    return response.data;
  }

  /**
   * Cross-folder scenario search — returns flat list of folders matching filters
   */
  async searchScenarioFolders(params: {    created_by?: string;
    ai_generated?: boolean;
    date_from?: string;
    date_to?: string;
    review_status?: string;
  }): Promise<{
    results: {
      folder_id: string;
      folder_name: string;
      folder_path: string;
      review_status: string;
      total: number;
      ai_count: number;
      manual_count: number;
      contributors: string[];
    }[];
    total_folders: number;
    total_scenarios: number;
  }> {
    const p = new URLSearchParams();
    if (params.created_by) p.append('created_by', params.created_by);
    if (params.ai_generated !== undefined) p.append('ai_generated', String(params.ai_generated));
    if (params.date_from) p.append('date_from', params.date_from);
    if (params.date_to) p.append('date_to', params.date_to);
    if (params.review_status) p.append('review_status', params.review_status);
    const response = await this.client.get(`/api/v1/folders/scenario-search?${p.toString()}`);
    return response.data;
  }

  /**
   * Update a scenario (creates new version)
   */
  async updateScenario(scenarioId: string, data: TestScenarioUpdate): Promise<TestScenario> {
    const response = await this.client.put<TestScenario>(`/api/v1/scenarios/${scenarioId}`, data);
    return response.data;
  }

  /**
   * Delete a scenario
   */
  async deleteScenario(scenarioId: string): Promise<void> {
    await this.client.delete(`/api/v1/scenarios/${scenarioId}`);
  }

  /**
   * Bulk delete scenarios
   */
  async bulkDeleteScenarios(scenarioIds: string[]): Promise<{ deleted_count: number; total_requested: number; failed_ids: string[] }> {
    const response = await this.client.post('/api/v1/scenarios/bulk-delete', scenarioIds);
    return response.data;
  }

  async bulkUpdateScenarios(payload: {
    scenario_ids: string[];
    priority?: string;
    environment?: string;
    tags_add?: string[];
    tags_remove?: string[];
    tags_set?: string[];
  }): Promise<{ updated_count: number; total_requested: number; failed_ids: string[] }> {
    const response = await this.client.post('/api/v1/scenarios/bulk-update', payload);
    return response.data;
  }

  /**
   * Get version history for a scenario
   */
  async getScenarioVersions(scenarioId: string): Promise<TestScenario[]> {
    const response = await this.client.get<TestScenario[]>(`/api/v1/scenarios/${scenarioId}/versions`);
    return response.data;
  }

  /**
   * Upload expected evidence for a scenario
   */
  async uploadScenarioEvidence(scenarioId: string, file: File, displayOrder: number): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('display_order', displayOrder.toString());

    await this.client.post(`/api/v1/scenarios/${scenarioId}/evidence`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Test Case API Methods

  /**
   * Create a test case manually - can be independent or linked to scenario
   */
  async createTestCaseManually(data: {
    folder_id: string;
    test_case_description: string;
    expected_result: string;
    preconditions?: string;
    priority?: 'Critical' | 'High' | 'Medium' | 'Low';
    steps?: string;
    tags?: string[];
    scenario_id?: string;
    scenario_version?: number;
  }): Promise<TestCase> {
    const response = await this.client.post<TestCase>('/api/v1/test-cases', data);
    return response.data;
  }

  /**
   * Update a test case
   */
  async updateTestCase(caseId: string, data: {
    test_case_description: string;
    preconditions?: string;
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    steps?: string;
    expected_result: string;
    tags?: string[];
  }): Promise<TestCase> {
    const response = await this.client.put<TestCase>(`/api/v1/test-cases/${caseId}`, data);
    return response.data;
  }

  /**
   * Export test scenarios to test cases
   */
  async exportTestCases(request: TestCaseExportRequest): Promise<TestCase[]> {
    const response = await this.client.post<TestCase[]>('/api/v1/test-cases/export', request);
    return response.data;
  }

  /**
   * Get all test cases, optionally filtered by folder
   */
  async getTestCases(folderId?: string, recursive: boolean = true): Promise<TestCase[]> {
    const params = new URLSearchParams();
    if (folderId) params.append('folder_id', folderId);
    if (recursive && folderId) params.append('recursive', 'true');
    const qs = params.toString();
    const response = await this.client.get<TestCase[]>(`/api/v1/test-cases${qs ? '?' + qs : ''}`);
    return response.data;
  }

  /**
   * Get a test case by ID
   */
  async getTestCase(caseId: string): Promise<TestCase> {
    const response = await this.client.get<TestCase>(`/api/v1/test-cases/${caseId}`);
    return response.data;
  }

  /**
   * Delete a test case
   */
  async deleteTestCase(caseId: string): Promise<void> {
    await this.client.delete(`/api/v1/test-cases/${caseId}`);
  }

  /**
   * Bulk delete test cases
   */
  async bulkDeleteTestCases(caseIds: string[]): Promise<{ deleted_count: number; total_requested: number; failed_ids: string[] }> {
    const response = await this.client.post('/api/v1/test-cases/bulk-delete', caseIds);
    return response.data;
  }

  async bulkUpdateTestCases(payload: {
    case_ids: string[];
    priority?: string;
    tags_add?: string[];
    tags_remove?: string[];
    tags_set?: string[];
  }): Promise<{ updated_count: number; total_requested: number; failed_ids: string[] }> {
    const response = await this.client.post('/api/v1/test-cases/bulk-update', payload);
    return response.data;
  }

  // Admin API Methods

  /**
   * Get all pending users (admin only)
   */
  async getPendingUsers(): Promise<any[]> {
    const response = await this.client.get('/api/v1/admin/users/pending');
    return response.data;
  }

  /**
   * Get all users (admin only)
   */
  async getAllUsers(): Promise<any[]> {
    const response = await this.client.get('/api/v1/admin/users');
    return response.data;
  }

  /**
   * Approve a user (admin only)
   */
  async approveUser(userEmail: string): Promise<any> {
    const response = await this.client.post(`/api/v1/admin/users/${userEmail}/approve`, {
      role: "Viewer"
    });
    return response.data;
  }

  /**
   * Reject a user (admin only)
   */
  async rejectUser(userEmail: string): Promise<void> {
    await this.client.post(`/api/v1/admin/users/${userEmail}/reject`);
  }

  /**
   * Deactivate a user (admin only)
   */
  async deactivateUser(userEmail: string): Promise<any> {
    const response = await this.client.put(`/api/v1/admin/users/${userEmail}/deactivate`);
    return response.data;
  }

  /**
   * Activate a user (admin only)
   */
  async activateUser(userEmail: string): Promise<any> {
    const response = await this.client.put(`/api/v1/admin/users/${userEmail}/activate`);
    return response.data;
  }

  /**
   * Create a new user directly (admin only)
   */
  async createUser(data: { name: string; email: string; role: string }): Promise<any> {
    const response = await this.client.post('/api/v1/admin/users', data);
    return response.data;
  }

  /**
   * Bulk import users from a CSV file (admin only)
   */
  async bulkImportUsers(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/api/v1/admin/users/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Download CSV template for bulk user import (admin only)
   */
  downloadBulkImportTemplate(): void {
    const content = 'name,email,role\nJohn Doe,john@company.com,QA_Engineer\nJane Smith,jane@company.com,Viewer\n';
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assurex_users_template.csv';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * AssureX AI Chatbot — send a message and get an answer
   */
  async chatbotMessage(message: string): Promise<{ answer: string }> {
    const response = await this.client.post('/api/v1/chatbot/message', { message });
    return response.data;
  }

  /**
   * Get user session tracker data (Admin only)
   */
  async getUserSessions(days: number = 30, userEmail?: string): Promise<any> {    const params = new URLSearchParams({ days: String(days) });
    if (userEmail) params.append('user_email', userEmail);
    const response = await this.client.get(`/api/v1/admin/sessions?${params.toString()}`);
    return response.data;
  }

  /**
   * Download full Test Data backup as ZIP (Admin only)
   */
  async downloadBackup(): Promise<void> {
    const response = await this.client.get('/api/v1/admin/backup', {
      responseType: 'blob',
    });
    const today = new Date().toISOString().slice(0, 10);
    const filename = `BACKUP - ${today} - ASSUREX.zip`;
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  /**
   * Update user role (admin only)
   */
  async updateUserRole(userEmail: string, role: string): Promise<any> {
    const response = await this.client.put(`/api/v1/admin/users/${userEmail}/role`, {
      role
    });
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    const response = await this.client.get('/healthcheck');
    return response.data;
  }
  /**
   * Import scenarios from Excel file
   */
  async importScenariosFromExcel(folderId: string, file: File): Promise<TestScenario[]> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post<TestScenario[]>(
      `/api/v1/import-export/scenarios/import?folder_id=${folderId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Download scenario import template
   */
  async downloadScenarioTemplate(): Promise<Blob> {
    const response = await this.client.get('/api/v1/import-export/scenarios/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Import test cases from Excel file
   */
  async importTestCasesFromExcel(folderId: string, file: File): Promise<TestCase[]> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post<TestCase[]>(
      `/api/v1/import-export/test-cases/import?folder_id=${folderId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Download test case import template
   */
  async downloadTestCaseTemplate(): Promise<Blob> {
    const response = await this.client.get('/api/v1/import-export/test-cases/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Import test cases from TestRail XML file
   */
  async importTestCasesFromXML(folderId: string, file: File): Promise<{
    success: boolean;
    summary: {
      folders_created: number;
      test_cases_created: number;
      test_cases_skipped: number;
      errors: Array<{
        type: string;
        name?: string;
        title?: string;
        reason: string;
      }>;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post(
      `/api/v1/import-export/test-cases/import-xml?folder_id=${folderId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // ==================== Test Run & Execution Methods ====================

  /**
   * Get distinct cycle labels that exist for a folder (for auto-fill)
   */
  async getCycleLabelsForFolder(folderId: string): Promise<string[]> {
    const response = await this.client.get(`/api/v1/test-runs/cycle-labels?folder_id=${folderId}`);
    return response.data.cycle_labels || [];
  }

  /**
   * Create a new test run from a folder
   */
  async createTestRun(data: {
    name: string;
    description?: string;
    folder_id: string;
    run_type?: 'SCENARIO' | 'TEST_CASE';
    build_version?: string;
    environment?: string;
    cycle_label?: string;
  }): Promise<any> {
    const response = await this.client.post('/api/v1/test-runs', data);
    return response.data;
  }

  /**
   * List all test runs with summary metrics
   */
  async listTestRuns(folderId?: string, statusFilter?: string): Promise<any[]> {
    const params: any = {};
    if (folderId) params.folder_id = folderId;
    if (statusFilter) params.status_filter = statusFilter;
    
    const response = await this.client.get('/api/v1/test-runs', { params });
    return response.data;
  }

  /**
   * Get test run details with progress
   */
  async getTestRun(testRunId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/test-runs/${testRunId}`);
    return response.data;
  }

  /**
   * Start a test run
   */
  async startTestRun(testRunId: string): Promise<any> {
    const response = await this.client.post(`/api/v1/test-runs/${testRunId}/start`);
    return response.data;
  }

  /**
   * Close a test run (make immutable)
   */
  async closeTestRun(testRunId: string): Promise<any> {
    const response = await this.client.post(`/api/v1/test-runs/${testRunId}/close`);
    return response.data;
  }

  /**
   * Reopen a COMPLETED or ABORTED test run (Admin only)
   */
  async reopenTestRun(testRunId: string): Promise<any> {
    const response = await this.client.post(`/api/v1/test-runs/${testRunId}/reopen`);
    return response.data;
  }

  /**
   * Get all executions for a test run
   */
  async getTestRunExecutions(testRunId: string, statusFilter?: string): Promise<any[]> {
    const params: any = {};
    if (statusFilter) params.status_filter = statusFilter;
    
    const response = await this.client.get(`/api/v1/test-runs/${testRunId}/executions`, { params });
    return response.data;
  }

  /**
   * Update test execution status
   */
  async updateExecution(executionId: string, data: {
    execution_status: string;
    comment?: string;
    defect_reference?: string;
  }): Promise<any> {
    const response = await this.client.put(`/api/v1/test-runs/executions/${executionId}`, data);
    return response.data;
  }

  /**
   * Upload execution evidence
   */
  async uploadExecutionEvidence(
    executionId: string,
    file: File,
    displayOrder: number = 0,
    onUploadProgress?: (percent: number) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post(
      `/api/v1/test-runs/executions/${executionId}/evidence?display_order=${displayOrder}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onUploadProgress
          ? (progressEvent) => {
              if (progressEvent.total) {
                onUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
              }
            }
          : undefined,
      }
    );
    return response.data;
  }

  /**
   * Get execution evidence
   */
  async getExecutionEvidence(executionId: string): Promise<any[]> {
    const response = await this.client.get(`/api/v1/test-runs/executions/${executionId}/evidence`);
    return response.data;
  }

  /**
   * Get a short-lived access URL for an evidence file (presigned S3 or local download)
   */
  async getEvidenceUrl(evidenceId: string): Promise<{ url: string | null; file_name: string; storage: string }> {
    const response = await this.client.get(`/api/v1/test-runs/evidence/${evidenceId}/url`);
    return response.data;
  }

  /**
   * Download evidence file
   */
  getEvidenceDownloadUrl(evidenceId: string): string {
    return `${API_BASE_URL}/api/v1/test-runs/evidence/${evidenceId}/download`;
  }

  /**
   * Rename a cycle label across all runs that share it
   */
  async renameCycleLabel(oldLabel: string, newLabel: string): Promise<{ updated: number; old_label: string; new_label: string }> {
    const params = new URLSearchParams({ old_label: oldLabel, new_label: newLabel });
    const response = await this.client.patch(`/api/v1/test-runs/cycle-label?${params.toString()}`);
    return response.data;
  }

  /**
   * Sync newly added scenarios/test-cases into an existing test run
   */
  async syncNewToTestRun(testRunId: string): Promise<{ added: number; message: string }> {
    const response = await this.client.post(`/api/v1/test-runs/${testRunId}/sync-new`);
    return response.data;
  }

  /**
   * Delete a test run (only if NOT_STARTED)
   */
  async deleteTestRun(testRunId: string): Promise<void> {
    await this.client.delete(`/api/v1/test-runs/${testRunId}`);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export as 'api' for convenience
export const api = apiClient;

// Export helper hook for loading states
export const createLoadingState = (): LoadingState => ({
  isLoading: false,
  error: null,
});
